/**
 * Hook to fetch concept name -> concept_code mapping from values/search API.
 * Used when creating/updating checklists with concepts that lack concept_code
 * (e.g. from DEFAULT_CONCEPTS_LIST).
 */

import { useState, useEffect, useCallback } from "react";
import { getConceptCodeMapping } from "../services/regulationsService";

/**
 * Returns concept name -> concept_code mapping (e.g. ProductType -> GCC-C000060).
 * Fetches from values/search when enabled.
 */
export function useConceptCodeMapping(enabled: boolean = true): {
  conceptCodeMapping: Record<string, string>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  const [conceptCodeMapping, setConceptCodeMapping] = useState<
    Record<string, string>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMapping = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const mapping = await getConceptCodeMapping();
      setConceptCodeMapping(mapping);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load concept mapping");
      setConceptCodeMapping({});
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    fetchMapping();
  }, [fetchMapping]);

  return {
    conceptCodeMapping,
    loading,
    error,
    refetch: fetchMapping,
  };
}
